import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Phone, Mic, FileText, Brain, Calendar, ChevronRight,
  Loader2, Sparkles, Copy, Check, ChevronDown, ChevronUp,
  Clock, AlertTriangle, CheckCircle, Target, Zap, Users,
  Shield, TrendingUp, MessageSquare, RotateCcw, ExternalLink,
  Play, ClipboardList, Radio,
} from 'lucide-react';
import voiceService, {
  PreCallBrief, CoachingPrompts, PostCallAnalysis,
  ExtractedStageData, NextCallBrief, ScheduledCall
} from './voiceService';
import apiClient from '@/services/api/apiClient';
import { useToast } from '@/components/common/Toast';
import { PageHeader } from '@/components/common/PageHeader';

// ── shared UI atoms ───────────────────────────────────────────────────────────
function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-xl border border-border bg-card ${className}`}>{children}</div>;
}

function Section({ title, icon: Icon, children, color = 'text-primary' }: {
  title: string; icon: any; children: React.ReactNode; color?: string;
}) {
  return (
    <Card className="p-4 space-y-3">
      <h4 className={`text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5 ${color}`}>
        <Icon className="h-3.5 w-3.5" />{title}
      </h4>
      {children}
    </Card>
  );
}

function CopyBtn({ text, className = '' }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className={`flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:bg-muted transition-colors ${className}`}
    >
      {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function RunButton({ onClick, loading, label, loadingLabel }: {
  onClick: () => void; loading: boolean; label: string; loadingLabel: string;
}) {
  return (
    <button onClick={onClick} disabled={loading}
      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors">
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
      {loading ? loadingLabel : label}
    </button>
  );
}

const DEAL_HEALTH_COLORS: Record<string, string> = {
  strong: 'text-emerald-600 dark:text-emerald-400',
  progressing: 'text-blue-600 dark:text-blue-400',
  stalled: 'text-amber-600 dark:text-amber-400',
  'at-risk': 'text-red-600 dark:text-red-400',
};

const INFLUENCE_COLORS: Record<string, string> = {
  champion: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
  'economic-buyer': 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
  influencer: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
  blocker: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400',
};

const OUTCOME_COLORS: Record<string, string> = {
  positive: 'bg-emerald-100 text-emerald-700',
  neutral: 'bg-blue-100 text-blue-700',
  negative: 'bg-red-100 text-red-700',
  rescheduled: 'bg-amber-100 text-amber-700',
  'no-show': 'bg-gray-100 text-gray-600',
};

const CALL_TYPE_LABELS: Record<string, string> = {
  CALL1: 'Alignment Call #1',
  CALL2: 'Confirmation Call #2',
  CALL3: 'Follow-up Call',
};

// ── Tab 1: Pre-Call Brief ─────────────────────────────────────────────────────
function PreCallBriefTab({ contactId, journeyId, stage }: { contactId: string; journeyId?: string; stage?: number }) {
  const { addToast } = useToast();
  const [callType, setCallType] = useState<'CALL1' | 'CALL2' | 'CALL3'>('CALL1');
  const [duration, setDuration] = useState(30);
  const [userNote, setUserNote] = useState('');
  const [brief, setBrief] = useState<PreCallBrief | null>(null);
  const [meta, setMeta] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [openSection, setOpenSection] = useState<string[]>(['objective', 'opening']);

  const toggle = (id: string) => setOpenSection(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const isOpen = (id: string) => openSection.includes(id);

  const run = async () => {
    if (!contactId) return addToast({ type: 'error', title: 'Select a contact first' });
    setLoading(true);
    try {
      const r = await voiceService.preCallBrief({ contactId, journeyId, stage, callType, callDurationMins: duration, userNote: userNote || undefined });
      setBrief(r.brief);
      setMeta(r.meta);
      setOpenSection(['objective', 'opening', 'guide']);
    } catch (e: any) {
      addToast({ type: 'error', title: 'Brief failed', description: e.response?.data?.error || e.message });
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      {/* Config */}
      <Card className="p-5 space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" /> Call Preparation
        </h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Call Type</label>
            <select value={callType} onChange={e => setCallType(e.target.value as any)}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background">
              {Object.entries(CALL_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Duration (mins)</label>
            <input type="number" value={duration} min={10} max={120}
              onChange={e => setDuration(Number(e.target.value))}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Prep note (optional)</label>
            <input value={userNote} onChange={e => setUserNote(e.target.value)}
              placeholder="e.g. 'they mentioned budget concerns'"
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background" />
          </div>
        </div>
        <RunButton onClick={run} loading={loading} label={`Generate ${CALL_TYPE_LABELS[callType]} Brief`} loadingLabel="Building brief…" />
      </Card>

      {brief && (
        <div className="space-y-3">
          {/* Call objective */}
          <Card className="p-4 bg-primary/5 border-primary/20">
            <div className="flex items-start gap-2">
              <Target className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-primary mb-1">Call Objective</p>
                <p className="text-sm font-medium">{brief.callObjective}</p>
              </div>
            </div>
          </Card>

          {/* Opening script */}
          <Card className="overflow-hidden">
            <button onClick={() => toggle('opening')} className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors">
              <span className="text-sm font-semibold flex items-center gap-2"><Play className="h-4 w-4 text-emerald-500" /> 30-Second Opening Script</span>
              {isOpen('opening') ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>
            {isOpen('opening') && (
              <div className="px-4 pb-4 border-t border-border pt-3 space-y-2">
                <div className="relative">
                  <pre className="text-sm whitespace-pre-wrap font-sans bg-muted/30 rounded-lg p-3 leading-relaxed italic">{brief.openingScript}</pre>
                  <CopyBtn text={brief.openingScript} className="absolute top-2 right-2" />
                </div>
              </div>
            )}
          </Card>

          {/* Minute-by-minute guide */}
          <Card className="overflow-hidden">
            <button onClick={() => toggle('guide')} className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors">
              <span className="text-sm font-semibold flex items-center gap-2"><Clock className="h-4 w-4 text-blue-500" /> Minute-by-Minute Guide</span>
              {isOpen('guide') ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>
            {isOpen('guide') && (
              <div className="px-4 pb-4 border-t border-border pt-3 space-y-2">
                {brief.minuteByMinuteGuide.map((g, i) => (
                  <div key={i} className="flex gap-3 p-2.5 rounded-lg bg-muted/30">
                    <span className="text-xs font-bold text-primary shrink-0 w-14 pt-0.5">{g.minutes}m</span>
                    <div>
                      <p className="text-sm font-medium">{g.focus}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Goal: {g.goal}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Talk track */}
          <Card className="overflow-hidden">
            <button onClick={() => toggle('talktrack')} className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors">
              <span className="text-sm font-semibold flex items-center gap-2"><MessageSquare className="h-4 w-4 text-violet-500" /> Talk Track</span>
              {isOpen('talktrack') ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>
            {isOpen('talktrack') && (
              <div className="px-4 pb-4 border-t border-border pt-3 space-y-3">
                {brief.talkTrack.map((t, i) => (
                  <div key={i} className="border border-border rounded-lg p-3 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="h-5 w-5 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                      <p className="text-xs font-semibold">{t.topic}</p>
                    </div>
                    <p className="text-sm text-foreground">{t.whatToSay}</p>
                    <p className="text-xs text-muted-foreground">→ Transition: {t.transitionTo}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Questions to ask */}
          <Card className="overflow-hidden">
            <button onClick={() => toggle('questions')} className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors">
              <span className="text-sm font-semibold flex items-center gap-2"><Brain className="h-4 w-4 text-amber-500" /> Questions to Ask ({brief.questionsToAsk.length})</span>
              {isOpen('questions') ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>
            {isOpen('questions') && (
              <div className="px-4 pb-4 border-t border-border pt-3 space-y-2">
                {brief.questionsToAsk.map((q, i) => (
                  <div key={i} className="flex gap-3 p-2.5 rounded-lg hover:bg-muted/30">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 mt-0.5 ${
                      q.when === 'early' ? 'bg-emerald-100 text-emerald-700' :
                      q.when === 'mid' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                    }`}>{q.when}</span>
                    <div>
                      <p className="text-sm font-medium">"{q.question}"</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{q.purpose}</p>
                    </div>
                    <CopyBtn text={q.question} className="ml-auto shrink-0 self-start" />
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Objection playbook */}
          <Card className="overflow-hidden">
            <button onClick={() => toggle('objections')} className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors">
              <span className="text-sm font-semibold flex items-center gap-2"><Shield className="h-4 w-4 text-orange-500" /> Objection Playbook</span>
              {isOpen('objections') ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>
            {isOpen('objections') && (
              <div className="px-4 pb-4 border-t border-border pt-3 space-y-3">
                {Object.entries(brief.objectionPlaybook).map(([obj, resp], i) => (
                  <div key={i} className="rounded-lg border border-orange-200 dark:border-orange-800 overflow-hidden">
                    <div className="bg-orange-50 dark:bg-orange-950/20 px-3 py-1.5 text-xs font-medium text-orange-700 dark:text-orange-400">
                      "/{obj}/"
                    </div>
                    <div className="px-3 py-2 flex gap-2">
                      <p className="text-sm flex-1">{resp}</p>
                      <CopyBtn text={resp} className="shrink-0 self-start" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Red flags + close move */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Section title="Red Flags to Watch" icon={AlertTriangle} color="text-red-500 dark:text-red-400">
              <ul className="space-y-1">
                {brief.redFlags.map((f, i) => (
                  <li key={i} className="text-sm flex items-start gap-1.5">
                    <span className="text-red-500 shrink-0">⚠</span>{f}
                  </li>
                ))}
              </ul>
            </Section>
            <Section title="How to Close the Call" icon={CheckCircle} color="text-emerald-600 dark:text-emerald-400">
              <p className="text-sm">{brief.closeMove}</p>
              <CopyBtn text={brief.closeMove} />
            </Section>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab 2: Live Coaching Prompts ───────────────────────────────────────────────
function CoachingTab({ contactId, journeyId, stage }: { contactId: string; journeyId?: string; stage?: number }) {
  const { addToast } = useToast();
  const [stageNum, setStageNum] = useState(stage || 6);
  const [currentTopic, setCurrentTopic] = useState('');
  const [userNote, setUserNote] = useState('');
  const [coaching, setCoaching] = useState<CoachingPrompts | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeCard, setActiveCard] = useState<number | null>(null);

  const run = async () => {
    if (!contactId) return addToast({ type: 'error', title: 'Select a contact first' });
    setLoading(true);
    try {
      const r = await voiceService.coachingPrompts({
        contactId, journeyId, stage: stageNum,
        currentTopic: currentTopic || undefined,
        userNote: userNote || undefined,
      });
      setCoaching(r.coaching);
      setActiveCard(null);
    } catch (e: any) {
      addToast({ type: 'error', title: 'Failed', description: e.response?.data?.error || e.message });
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <Card className="p-5 space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Radio className="h-4 w-4 text-red-500" />
          Live Call Coaching Prompts
          <span className="ml-1 text-xs font-normal text-muted-foreground">— generate before or during the call</span>
        </h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Call Stage</label>
            <select value={stageNum} onChange={e => setStageNum(Number(e.target.value))}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background">
              {Array.from({ length: 14 }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>Stage {n}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Current topic (live)</label>
            <input value={currentTopic} onChange={e => setCurrentTopic(e.target.value)}
              placeholder="e.g. 'discussing their SAP issues'"
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Live note</label>
            <input value={userNote} onChange={e => setUserNote(e.target.value)}
              placeholder="e.g. 'they seem hesitant'"
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background" />
          </div>
        </div>
        <RunButton onClick={run} loading={loading} label="Generate 6 Coaching Cards" loadingLabel="Building cards…" />
      </Card>

      {coaching && (
        <div className="space-y-4">
          {/* Stage focus */}
          <Card className="p-3 bg-primary/5 border-primary/20">
            <p className="text-xs font-semibold text-primary mb-0.5">Call Focus</p>
            <p className="text-sm">{coaching.stageFocus}</p>
          </Card>

          {/* Prompt cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {coaching.promptCards.map((card) => (
              <div key={card.id}
                onClick={() => setActiveCard(activeCard === card.id ? null : card.id)}
                className={`rounded-xl border cursor-pointer transition-all ${
                  activeCard === card.id
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border bg-card hover:border-primary/40 hover:shadow-sm'
                }`}
              >
                <div className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">
                      {card.id}
                    </span>
                    <p className="text-sm font-semibold">{card.title}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">Use when: {card.trigger}</p>
                </div>

                {activeCard === card.id && (
                  <div className="border-t border-border px-3 pb-3 pt-2 space-y-2.5">
                    <div className="rounded-lg bg-muted/50 p-2.5">
                      <p className="text-[10px] font-semibold text-muted-foreground mb-1">SAY THIS</p>
                      <p className="text-sm italic">"{card.phrase}"</p>
                      <CopyBtn text={card.phrase} className="mt-1.5" />
                    </div>
                    <div className="rounded-lg bg-amber-50 dark:bg-amber-950/20 p-2.5">
                      <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 mb-1">THEN ASK</p>
                      <p className="text-sm">"{card.probeQuestion}"</p>
                      <CopyBtn text={card.probeQuestion} className="mt-1.5" />
                    </div>
                    <p className="text-xs text-muted-foreground">Goal: {card.goal}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Signals + pivot */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Section title="Buying Signals to Listen For" icon={TrendingUp} color="text-emerald-600">
              <ul className="space-y-1">
                {coaching.buyingSignalPhrases.map((s, i) => (
                  <li key={i} className="text-xs text-foreground flex items-start gap-1.5">
                    <CheckCircle className="h-3 w-3 text-emerald-500 shrink-0 mt-0.5" />{s}
                  </li>
                ))}
              </ul>
            </Section>
            <Section title="Danger Signals" icon={AlertTriangle} color="text-red-500">
              <ul className="space-y-1">
                {coaching.dangerSignals.map((s, i) => (
                  <li key={i} className="text-xs text-foreground flex items-start gap-1.5">
                    <span className="text-red-500 shrink-0">⚠</span>{s}
                  </li>
                ))}
              </ul>
            </Section>
            <Section title="If Call Goes Off-Track" icon={RotateCcw} color="text-amber-600">
              <p className="text-sm">{coaching.pivotMove}</p>
              <CopyBtn text={coaching.pivotMove} />
            </Section>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab 3: Post-Call Analysis ─────────────────────────────────────────────────
function PostCallTab({ contactId, journeyId, stage }: { contactId: string; journeyId?: string; stage?: number }) {
  const { addToast } = useToast();
  const [callType, setCallType] = useState<'CALL1' | 'CALL2' | 'CALL3'>('CALL1');
  const [outcome, setOutcome] = useState<'positive' | 'neutral' | 'negative' | 'rescheduled' | 'no-show'>('positive');
  const [notes, setNotes] = useState('');
  const [transcript, setTranscript] = useState('');
  const [duration, setDuration] = useState(30);
  const [userNote, setUserNote] = useState('');
  const [result, setResult] = useState<PostCallAnalysis | null>(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!notes.trim()) return addToast({ type: 'error', title: 'Error', description: 'Add call notes first' });
    if (!contactId) return addToast({ type: 'error', title: 'Select a contact first' });
    setLoading(true);
    try {
      const r = await voiceService.postCallAnalysis({
        contactId, journeyId, stage, callType, notes, outcome,
        transcript: transcript || undefined,
        callDurationMins: duration,
        userNote: userNote || undefined,
      });
      setResult(r.analysis);
    } catch (e: any) {
      addToast({ type: 'error', title: 'Analysis failed', description: e.response?.data?.error || e.message });
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <Card className="p-5 space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-primary" /> Post-Call Analysis
        </h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Call Type</label>
            <select value={callType} onChange={e => setCallType(e.target.value as any)}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background">
              {Object.entries(CALL_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Outcome</label>
            <select value={outcome} onChange={e => setOutcome(e.target.value as any)}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background">
              {['positive','neutral','negative','rescheduled','no-show'].map(o => (
                <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Duration (mins)</label>
            <input type="number" value={duration} min={1}
              onChange={e => setDuration(Number(e.target.value))}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background" />
          </div>
        </div>

        <div>
          <label className="text-xs text-muted-foreground block mb-1">Call Notes <span className="text-foreground">*</span></label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Raw notes from the call — what was discussed, what was confirmed, what they said, any objections or commitments…"
            rows={6}
            className="w-full text-sm px-3 py-2 rounded-md border border-border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/40" />
        </div>

        <div>
          <label className="text-xs text-muted-foreground block mb-1">Transcript (optional — paste if available)</label>
          <textarea value={transcript} onChange={e => setTranscript(e.target.value)}
            placeholder="Full call transcript if available (will improve accuracy)…"
            rows={3}
            className="w-full text-sm px-3 py-2 rounded-md border border-border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/40" />
        </div>

        <textarea value={userNote} onChange={e => setUserNote(e.target.value)}
          placeholder="Your reflection (optional — e.g. 'felt like there was a budget issue they didn't mention')"
          rows={2}
          className="w-full text-sm px-3 py-2 rounded-md border border-border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/40" />

        <RunButton onClick={run} loading={loading} label="Analyse Call" loadingLabel="Analysing…" />
      </Card>

      {result && (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex flex-wrap items-center gap-3">
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${OUTCOME_COLORS[result.outcome] || 'bg-muted text-muted-foreground'}`}>
              {result.outcome}
            </span>
            <span className={`text-sm font-semibold ${DEAL_HEALTH_COLORS[result.dealHealth]}`}>
              Deal: {result.dealHealth}
            </span>
            <span className="text-xs text-muted-foreground">· {result.dealHealthReason}</span>
            <span className="ml-auto text-xs text-muted-foreground">Urgency {result.urgencyScore}/10</span>
            {result._journeyUpdated && (
              <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                <CheckCircle className="h-3 w-3" /> Journey auto-updated
              </span>
            )}
          </div>

          {/* Summary */}
          <Card className="p-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Call Summary</p>
            <p className="text-sm leading-relaxed">{result.callSummary}</p>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Confirmed facts */}
            {result.confirmedFacts.length > 0 && (
              <Section title="Confirmed Facts" icon={CheckCircle} color="text-emerald-600">
                <ul className="space-y-1.5">
                  {result.confirmedFacts.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 mt-0.5 ${
                        f.category === 'pain' ? 'bg-orange-100 text-orange-700' :
                        f.category === 'stack' ? 'bg-blue-100 text-blue-700' :
                        f.category === 'budget' ? 'bg-amber-100 text-amber-700' :
                        f.category === 'stakeholder' ? 'bg-purple-100 text-purple-700' :
                        'bg-muted text-muted-foreground'
                      }`}>{f.category}</span>
                      {f.fact}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* New insights */}
            {result.newInsights.length > 0 && (
              <Section title="New Insights" icon={Sparkles} color="text-violet-600">
                <ul className="space-y-1">
                  {result.newInsights.map((n, i) => <li key={i} className="text-sm">→ {n}</li>)}
                </ul>
              </Section>
            )}

            {/* Objections */}
            {result.objectionsRaised.length > 0 && (
              <Section title="Objections Raised" icon={AlertTriangle} color="text-orange-500">
                <div className="space-y-2">
                  {result.objectionsRaised.map((o, i) => (
                    <div key={i} className="text-sm">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">"{o.objection}"</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${o.resolved ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {o.resolved ? 'resolved' : 'open'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{o.response}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Stakeholders */}
            {result.stakeholdersIdentified.length > 0 && (
              <Section title="Stakeholders Identified" icon={Users} color="text-blue-600">
                <div className="space-y-1.5">
                  {result.stakeholdersIdentified.map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${INFLUENCE_COLORS[s.influence] || 'bg-muted text-muted-foreground'}`}>
                        {s.influence}
                      </span>
                      <p className="text-sm">{s.name} <span className="text-muted-foreground">— {s.role}</span></p>
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </div>

          {/* Commitments */}
          {result.commitmentsMade.length > 0 && (
            <Section title="Commitments Made" icon={ClipboardList} color="text-primary">
              <div className="space-y-1.5">
                {result.commitmentsMade.map((c, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 mt-0.5 ${c.who === 'mda' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      {c.who}
                    </span>
                    <span className="flex-1">{c.commitment}</span>
                    {c.deadline && <span className="text-xs text-muted-foreground shrink-0">{c.deadline}</span>}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Stage advancement + journey field updates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card className="p-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Stage Advancement</p>
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${result.stageAdvancement.recommended && result.stageAdvancement.direction === 'forward' ? 'bg-emerald-500' : result.stageAdvancement.direction === 'backward' ? 'bg-red-500' : 'bg-amber-500'}`} />
                <span className="text-sm font-medium capitalize">{result.stageAdvancement.direction}</span>
                {result.stageAdvancement.recommended && result.stageAdvancement.newStage > 0 && (
                  <span className="text-xs text-muted-foreground">→ Stage {result.stageAdvancement.newStage}</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{result.stageAdvancement.reason}</p>
            </Card>

            <Card className="p-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Next Call Recommendation</p>
              <p className="text-xs"><span className="font-medium">When:</span> {result.nextCallRecommendation.timing}</p>
              <p className="text-xs"><span className="font-medium">Objective:</span> {result.nextCallRecommendation.objective}</p>
              <p className="text-xs text-muted-foreground">{result.nextCallRecommendation.prepNeeded}</p>
            </Card>
          </div>

          {/* Journey field updates — copy-paste ready */}
          <Card className="p-4 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5 text-primary" /> Journey Form — Auto-Filled Content
            </p>
            {Object.entries(result.journeyFieldUpdates).filter(([, v]) => v).map(([field, val]) => (
              <div key={field}>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-muted-foreground capitalize">{field}</label>
                  <CopyBtn text={val} />
                </div>
                <p className="text-sm bg-muted/30 rounded-lg px-3 py-2 leading-relaxed">{val}</p>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}

// ── Tab 4: Extract Stage Data ─────────────────────────────────────────────────
function ExtractTab({ contactId, journeyId, stage }: { contactId: string; journeyId?: string; stage?: number }) {
  const { addToast } = useToast();
  const [stageNum, setStageNum] = useState(stage || 6);
  const [rawNotes, setRawNotes] = useState('');
  const [userNote, setUserNote] = useState('');
  const [result, setResult] = useState<ExtractedStageData | null>(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!rawNotes.trim()) return addToast({ type: 'error', title: 'Error', description: 'Paste your call notes' });
    if (!contactId) return addToast({ type: 'error', title: 'Select a contact first' });
    setLoading(true);
    try {
      const r = await voiceService.extractStageData({ contactId, journeyId, stage: stageNum, rawNotes, userNote: userNote || undefined });
      setResult(r.extracted);
    } catch (e: any) {
      addToast({ type: 'error', title: 'Failed', description: e.response?.data?.error || e.message });
    } finally { setLoading(false); }
  };

  const CONFIDENCE_COLORS: Record<string, string> = {
    high: 'bg-emerald-100 text-emerald-700',
    medium: 'bg-amber-100 text-amber-700',
    low: 'bg-red-100 text-red-700',
  };

  const FIELDS = ['activities', 'response', 'nextSteps', 'notes', 'trigger'] as const;
  const FIELD_LABELS: Record<string, string> = {
    activities: 'Key Activities',
    response: 'Contact Response',
    nextSteps: 'Next Steps',
    notes: 'Notes',
    trigger: 'Trigger (Stage 1 only)',
  };

  return (
    <div className="space-y-5">
      <Card className="p-5 space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Zap className="h-4 w-4 text-primary" /> Auto-Fill Journey Stage from Notes
        </h3>
        <p className="text-xs text-muted-foreground">Paste rough call notes — the AI extracts and maps them to the correct ABM journey form fields automatically.</p>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Journey Stage to Fill</label>
            <select value={stageNum} onChange={e => setStageNum(Number(e.target.value))}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background">
              {Array.from({ length: 14 }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>Stage {n}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Context (optional)</label>
            <input value={userNote} onChange={e => setUserNote(e.target.value)}
              placeholder="e.g. 'this was a pre-call email exchange'"
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background" />
          </div>
        </div>

        <textarea value={rawNotes} onChange={e => setRawNotes(e.target.value)}
          placeholder="Paste raw call notes here — bullet points, sentence fragments, whatever you wrote during the call. The AI handles the rest."
          rows={8}
          className="w-full text-sm px-3 py-2 rounded-md border border-border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/40" />

        <RunButton onClick={run} loading={loading} label={`Extract Stage ${stageNum} Data`} loadingLabel="Extracting…" />
      </Card>

      {result && (
        <div className="space-y-4">
          {/* Confidence + saved */}
          <div className="flex items-center gap-3">
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${CONFIDENCE_COLORS[result.confidence]}`}>
              {result.confidence} confidence extraction
            </span>
            {result._saved && (
              <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                <CheckCircle className="h-3 w-3" /> Auto-saved to journey
              </span>
            )}
          </div>

          {/* Extracted fields */}
          <div className="space-y-3">
            {FIELDS.map(field => {
              const val = result[field];
              if (!val) return null;
              return (
                <Card key={field} className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{FIELD_LABELS[field]}</label>
                    <CopyBtn text={val} />
                  </div>
                  <p className="text-sm leading-relaxed bg-muted/30 rounded-lg px-3 py-2">{val}</p>
                </Card>
              );
            })}
          </div>

          {/* Missing info */}
          {result.missingInfo.length > 0 && (
            <Card className="p-4 space-y-2 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> Missing Information — Follow Up
              </p>
              <ul className="space-y-1">
                {result.missingInfo.map((m, i) => <li key={i} className="text-sm text-amber-900 dark:text-amber-300">→ {m}</li>)}
              </ul>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// ── Tab 5: Next Call Brief ────────────────────────────────────────────────────
function NextCallTab({ contactId, journeyId }: { contactId: string; journeyId?: string }) {
  const { addToast } = useToast();
  const [userNote, setUserNote] = useState('');
  const [result, setResult] = useState<{ nextCall: NextCallBrief; meta: any } | null>(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!contactId) return addToast({ type: 'error', title: 'Select a contact first' });
    setLoading(true);
    try {
      const r = await voiceService.nextCallBrief({ contactId, journeyId, userNote: userNote || undefined });
      setResult(r);
    } catch (e: any) {
      addToast({ type: 'error', title: 'Failed', description: e.response?.data?.error || e.message });
    } finally { setLoading(false); }
  };

  const nc = result?.nextCall;

  return (
    <div className="space-y-5">
      <Card className="p-5 space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <ChevronRight className="h-4 w-4 text-primary" /> Next Call Brief
        </h3>
        <p className="text-xs text-muted-foreground">Builds on everything established in prior stages — not a generic checklist, but a brief specific to where this deal actually is.</p>
        <textarea value={userNote} onChange={e => setUserNote(e.target.value)}
          placeholder="Optional context (e.g. 'they hinted at a Q3 budget cycle')"
          rows={2}
          className="w-full text-sm px-3 py-2 rounded-md border border-border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/40" />
        <RunButton onClick={run} loading={loading} label="Generate Next Call Brief" loadingLabel="Building brief…" />
      </Card>

      {nc && (
        <div className="space-y-4">
          {/* Context */}
          <Card className="p-4 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Call Context</p>
            <p className="text-sm leading-relaxed">{nc.callContext}</p>
          </Card>

          {/* Opening line */}
          <Card className="p-4 space-y-2 bg-primary/5 border-primary/20">
            <p className="text-xs font-semibold text-primary uppercase tracking-wide flex items-center gap-1.5">
              <Play className="h-3 w-3" /> Opening Line
            </p>
            <p className="text-sm italic">"{nc.openingLine}"</p>
            <CopyBtn text={nc.openingLine} />
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section title="Proposed Agenda" icon={ClipboardList} color="text-blue-600">
              <ol className="space-y-1">
                {nc.agendaToPropose.map((a, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <span className="text-muted-foreground shrink-0">{i + 1}.</span>{a}
                  </li>
                ))}
              </ol>
            </Section>

            <Section title="Hypotheses to Validate" icon={Brain} color="text-violet-600">
              <ul className="space-y-1">
                {nc.hypothesesToValidate.map((h, i) => (
                  <li key={i} className="text-sm">→ {h}</li>
                ))}
              </ul>
            </Section>

            {nc.unresolvedObjections.length > 0 && (
              <Section title="Unresolved Objections" icon={AlertTriangle} color="text-orange-500">
                <ul className="space-y-1">
                  {nc.unresolvedObjections.map((o, i) => (
                    <li key={i} className="text-sm flex items-start gap-1.5">
                      <span className="text-orange-500 shrink-0">⚠</span>{o}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            <Section title="Risk to Monitor" icon={Shield} color="text-red-500">
              <p className="text-sm">{nc.riskToMonitor}</p>
            </Section>
          </div>

          <Section title="Content to Send Before the Call" icon={Zap} color="text-amber-600">
            <p className="text-sm">{nc.contentToSendBeforeCall}</p>
            <CopyBtn text={nc.contentToSendBeforeCall} />
          </Section>

          <Card className="p-4 bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800">
            <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-1">Call Success Metric</p>
            <p className="text-sm font-medium">{nc.successMetric}</p>
          </Card>
        </div>
      )}
    </div>
  );
}

// ── Tab 6: Scheduled Calls ────────────────────────────────────────────────────
function ScheduledTab() {
  const [data, setData] = useState<ScheduledCall[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await voiceService.getScheduled();
      setData(r.data);
    } catch { setData([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const CALL_TYPE_COLORS: Record<string, string> = {
    CALL1: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
    CALL2: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400',
    CALL3: 'bg-gray-100 text-gray-600 dark:bg-gray-900/40 dark:text-gray-400',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Upcoming calls in the next 7 days — those within 24h are flagged for prep.</p>
        <button onClick={load} className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors">
          <RotateCcw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : data.length === 0 ? (
        <Card className="p-8 text-center">
          <Calendar className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm font-medium">No calls scheduled in the next 7 days</p>
          <p className="text-xs text-muted-foreground mt-1">Schedule call tasks from the Call Management page.</p>
          <a href="/calls" className="inline-flex items-center gap-1 mt-3 text-xs text-primary hover:underline">
            <ExternalLink className="h-3 w-3" /> Go to Call Management
          </a>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.map(call => (
            <Card key={call.taskId}
              className={`p-4 flex items-center gap-4 ${call.needsPrep ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/10' : ''}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-semibold">{call.contactName}</p>
                  <span className="text-xs text-muted-foreground">·</span>
                  <p className="text-xs text-muted-foreground truncate">{call.accountName}</p>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${CALL_TYPE_COLORS[call.callType] || 'bg-muted text-muted-foreground'}`}>
                    {CALL_TYPE_LABELS[call.callType] || call.callType}
                  </span>
                  {call.needsPrep && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400 flex items-center gap-0.5">
                      <Clock className="h-2.5 w-2.5" /> Prep needed
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span><Clock className="h-3 w-3 inline mr-0.5" />{new Date(call.dueDate).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  <span>{call.hoursUntil > 0 ? `${call.hoursUntil}h away` : 'Now'}</span>
                  <span>{call.assignedTo}</span>
                  <span>{call.industry} · {call.region}</span>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <a href={`/voice-agent?contactId=${call.contactId}`}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                  <FileText className="h-3 w-3" /> Get Brief
                </a>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'brief',     label: 'Pre-Call Brief',     icon: FileText },
  { id: 'coaching',  label: 'Live Coaching',       icon: Radio },
  { id: 'postcall',  label: 'Post-Call Analysis',  icon: ClipboardList },
  { id: 'extract',   label: 'Extract Stage Data',  icon: Zap },
  { id: 'nextcall',  label: 'Next Call Brief',     icon: ChevronRight },
  { id: 'scheduled', label: 'Scheduled Calls',     icon: Calendar },
];

export default function VoiceAgentPage() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'brief');
  const [contacts, setContacts] = useState<any[]>([]);
  const [journeys, setJourneys] = useState<any[]>([]);
  const [selectedContact, setSelectedContact] = useState(searchParams.get('contactId') || '');
  const [selectedJourney, setSelectedJourney] = useState(searchParams.get('journeyId') || '');
  const [stage, setStage] = useState<number>(parseInt(searchParams.get('stage') || '0') || 0);

  useEffect(() => {
    apiClient.get('/api/contacts?limit=100').then(r => setContacts(r.data?.data || []));
  }, []);

  const loadJourney = useCallback(async (cId: string) => {
    try {
      const r = await apiClient.get(`/api/abm-journeys/contact/${cId}`);
      if (r.data?.id) {
        setJourneys([r.data]);
        setSelectedJourney(r.data.id);
        setStage(r.data.currentStage || 1);
      }
    } catch { setJourneys([]); }
  }, []);

  useEffect(() => {
    if (selectedContact) loadJourney(selectedContact);
  }, [selectedContact, loadJourney]);

  const commonProps = { contactId: selectedContact, journeyId: selectedJourney || undefined, stage: stage || undefined };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <PageHeader
        title="Voice Call Agent"
        description="AI-powered call intelligence — pre-call briefs, live coaching, post-call analysis, stage auto-fill, and next call prep"
        icon={Phone}
      />

      {/* Contact / Journey selector */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Contact</label>
            <select value={selectedContact} onChange={e => { setSelectedContact(e.target.value); setSelectedJourney(''); setStage(0); }}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background">
              <option value="">— Select contact —</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>{c.firstName} {c.lastName} — {c.account?.name || ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Journey</label>
            <select value={selectedJourney} onChange={e => setSelectedJourney(e.target.value)}
              disabled={!journeys.length}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background disabled:opacity-50">
              <option value="">{journeys.length ? '— Select journey —' : '— No journey —'}</option>
              {journeys.map(j => <option key={j.id} value={j.id}>Stage {j.currentStage}/14 · {j.completedStages?.length || 0} done</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Current Stage</label>
            <select value={stage} onChange={e => setStage(Number(e.target.value))}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background">
              <option value={0}>— Auto —</option>
              {Array.from({ length: 14 }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>Stage {n}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted/50 rounded-xl p-1 overflow-x-auto">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}>
            <tab.icon className="h-3.5 w-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'brief'     && <PreCallBriefTab  {...commonProps} />}
      {activeTab === 'coaching'  && <CoachingTab       {...commonProps} stage={stage || 6} />}
      {activeTab === 'postcall'  && <PostCallTab       {...commonProps} />}
      {activeTab === 'extract'   && <ExtractTab        {...commonProps} stage={stage || 6} />}
      {activeTab === 'nextcall'  && <NextCallTab       contactId={selectedContact} journeyId={selectedJourney || undefined} />}
      {activeTab === 'scheduled' && <ScheduledTab />}
    </div>
  );
}
